import { ReactNode, useState } from 'react';
import OutlinedTextField from '@alltrails/shared/components/OutlinedTextField';
import Button from '@alltrails/shared/denali/components/Button';
import Typography from '@alltrails/shared/denali/components/Typography';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { post } from '@alltrails/shared/api';
import Toast from '@alltrails/shared/denali/components/Toast';
import useToggle from '@alltrails/shared/hooks/useToggle';
import isValidEmail from 'utils/isValidEmail';
import * as styles from './ForgotPasswordForm.module.scss';
import { wrapUrlSafe } from '../../../utils/language_support_util';

type ForgotPasswordResponse = {
  redirectUrl: string;
};

type Props = {
  forgotPasswordUrl: string;
  resourceName: string;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=6618%3A99493
export default function ForgotPasswordForm({ forgotPasswordUrl, resourceName }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [showErrorsOnType, setShowErrorsOnType] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [error, setError] = useState<ReactNode>();
  const languageRegionCode = useLanguageRegionCode();
  const [serverError, setServerError] = useState<ReactNode>();
  const [showToast, toggleShowToast] = useToggle();

  function failedFormValidation() {
    if (!isValidEmail(forgotPasswordEmail)) {
      setError(<FormattedMessage defaultMessage="Email is not valid." />);
      return true;
    }

    setError(undefined);
    return false;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (submitting) {
      return;
    }

    if (failedFormValidation()) {
      setShowErrorsOnType(true);
      return;
    }

    setSubmitting(true);

    const params = {
      email: forgotPasswordEmail
    };

    try {
      await post<ForgotPasswordResponse>(forgotPasswordUrl, params);

      toggleShowToast();

      setTimeout(() => {
        window.location.assign(wrapUrlSafe('/login', languageRegionCode));
      }, 3000);
    } catch (e) {
      setServerError(<FormattedMessage defaultMessage="There was an error sending the password reset email." />);
      setSubmitting(false);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;

    if (showErrorsOnType) {
      if (!isValidEmail(value)) {
        setError(<FormattedMessage defaultMessage="Email is not valid." />);
      } else {
        setError(undefined);
      }
    }

    setForgotPasswordEmail(value);
  }

  return (
    <form className={styles.content} onSubmit={handleSubmit}>
      {showToast && <Toast message={<FormattedMessage defaultMessage="Email sent" />} testId="email-sent" type="success" />}
      <div className={styles.heading}>
        <Typography component="div" variant="heading400">
          <FormattedMessage defaultMessage="Forgot your password?" />
        </Typography>
        <Typography component="div" variant="text200">
          <FormattedMessage defaultMessage="Enter your email to get help logging in." />
        </Typography>
      </div>

      <OutlinedTextField
        name="forgotPasswordEmail"
        errorMessage={error}
        value={forgotPasswordEmail}
        label={<FormattedMessage defaultMessage="Email" />}
        changeHandler={handleChange}
        type="email"
      />
      <div>
        <div className={styles.error}>{serverError}</div>
        <Button
          disabled={submitting}
          testId="forgot-password-submit"
          text={<FormattedMessage defaultMessage="Send email" />}
          type="submit"
          variant="primary"
          width="full"
        />
      </div>
      <Button
        text={<FormattedMessage defaultMessage="Back to sign in" />}
        linkInfo={{ href: wrapUrlSafe('/login', languageRegionCode) }}
        testId="forgot-password-back-to-sign-in"
        variant="flat"
        width="full"
      />
      <Typography color="secondary" component="div" variant="paragraph200">
        <FormattedMessage defaultMessage="Note: The password reset email may take a few minutes. If you don't see it then, please check your “Promotions” tab and spam folder. If you are resubmitting this form, please note that any previous password reset emails you have requested will not work." />
      </Typography>
    </form>
  );
}
